'use client';

import { c as _c } from "react/compiler-runtime";
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import { trackCtaPeriod } from '@feat/feat-tracking/cta';
import { trackWebsiteMessage } from '@feat/feat-tracking/message';
import { formatIsoDate } from '@utils/utils-format';
import { DotPulse } from 'ldrs/react';
import 'ldrs/react/DotPulse.css';
import { getTodayInDefaultTimeZone, parseLocalDate } from '@utils/helpers';
import { useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { edpTimeslotSelectionAtom } from '../_lib/edp-booking-atoms';
import { AppCartNoDates } from './app-cart-no-dates';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceRequestDateBox } from './app-experience-request-date-box';
const DateRangePicker = dynamic(() => import('@7f-ui/components/datepicker/date-range-picker/date-range-picker').then(module => module.DateRangePicker), {
  ssr: false,
  loading: () => <div className="grid h-full place-content-center sm:min-h-96">
        <DotPulse speed={1.3} color="currentColor" />
      </div>
});
type AppDateModalContentProps = {
  availableDates?: string[];
  onClose: (isConfirmed?: boolean) => void;
  userTimeslotRequestsAllowed?: boolean | null;
};
export function AppDateModalContent(t0) {
  const $ = _c(36);
  const {
    onClose,
    availableDates,
    userTimeslotRequestsAllowed
  } = t0;
  const {
    filter,
    experience,
    setFilter,
    variants
  } = useExperienceFilterContext();
  const setTimeslotSelection = useSetAtom(edpTimeslotSelectionAtom);
  const t1 = filter.from ?? null;
  const t2 = filter.to ?? null;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = [t1, t2];
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [dates, setDates] = useState(t3);
  let t4;
  if ($[3] !== dates || $[4] !== filter || $[5] !== onClose || $[6] !== setFilter || $[7] !== setTimeslotSelection) {
    t4 = () => {
      const [from, to] = dates;
      setFilter({
        ...filter,
        from,
        to
      });
      setTimeslotSelection(null);
      onClose(true);
      trackCtaPeriod(from, to, "experience");
    };
    $[3] = dates;
    $[4] = filter;
    $[5] = onClose;
    $[6] = setFilter;
    $[7] = setTimeslotSelection;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const confirmDateSelection = t4;
  let t5;
  if ($[9] !== filter.selectedOptions) {
    t5 = range => {
      const newDates = range ?? [null, null];
      trackWebsiteMessage("experience", {
        from: newDates[0],
        to: newDates[1],
        adults: Object.values(filter?.selectedOptions ?? {})[0],
        children: Object.values(filter?.selectedOptions ?? {})[1]
      });
      setDates(newDates);
    };
    $[9] = filter.selectedOptions;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const onChange = t5;
  const hasAvailableDateInRange = t6 => {
    const [from_0, to_0] = t6;
    if (!availableDates?.length || !from_0) {
      return true;
    }
    const start = formatIsoDate(from_0);
    const end = to_0 ? formatIsoDate(to_0) : start;
    return availableDates.some(date => date >= start && date <= end);
  };
  const isSelectionUnavailable = !hasAvailableDateInRange(dates);
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = getTodayInDefaultTimeZone();
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const today = t7;
  let t8;
  if ($[12] !== experience.offerPeriodStart) {
    t8 = experience.offerPeriodStart ? parseLocalDate(experience.offerPeriodStart) : today;
    $[12] = experience.offerPeriodStart;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const minDate = t8;
  let t9;
  if ($[14] !== experience.offerPeriodEnd) {
    t9 = experience.offerPeriodEnd?.local ? parseLocalDate(experience.offerPeriodEnd) : today;
    $[14] = experience.offerPeriodEnd;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const maxDate = t9;
  const showRequestDateBox = userTimeslotRequestsAllowed && !isSelectionUnavailable;
  let t10;
  if ($[16] !== availableDates?.length || $[17] !== confirmDateSelection || $[18] !== experience.publicId || $[19] !== isSelectionUnavailable || $[20] !== showRequestDateBox) {
    t10 = () => <Modal.Footer className="flex flex-col gap-y-8">{isSelectionUnavailable && <MessageBox type="error">Keine freien Termine gefunden. Bitte ändere den Zeitraum.</MessageBox>}{showRequestDateBox && <AppExperienceRequestDateBox publicId={experience.publicId} />}<Modal.Footer.Actions><Modal.Footer.Button disabled={availableDates?.length === 0 || isSelectionUnavailable} onClick={confirmDateSelection}>Übernehmen</Modal.Footer.Button></Modal.Footer.Actions></Modal.Footer>;
    $[16] = availableDates?.length;
    $[17] = confirmDateSelection;
    $[18] = experience.publicId;
    $[19] = isSelectionUnavailable;
    $[20] = showRequestDateBox;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== availableDates || $[23] !== dates || $[24] !== maxDate || $[25] !== minDate || $[26] !== onChange) {
    t11 = availableDates?.length === 0 ? <AppCartNoDates /> : <DateRangePicker value={dates} min={minDate} max={maxDate} onChange={onChange} allowsNonContiguousRanges={true} isDateUnavailable={date_0 => !availableDates?.includes(date_0.toString())} />;
    $[22] = availableDates;
    $[23] = dates;
    $[24] = maxDate;
    $[25] = minDate;
    $[26] = onChange;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== variants.length) {
    t12 = variants.length > 1 && <MessageBox type="info" className="mx-4">Die Auswahlmöglichkeiten und Preise hängen von der gewählten Option ab.</MessageBox>;
    $[28] = variants.length;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <div className="h-full">{t11}{t12}</div>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t10 || $[34] !== t13) {
    t14 = <Modal.Content className="px-0 py-4" renderFooter={t10}>{t13}</Modal.Content>;
    $[33] = t10;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}
type AppDateModalProps = AppDateModalContentProps & {
  isOpen: boolean;
};
export function AppDateModal(t0) {
  const $ = _c(8);
  const {
    isOpen,
    onClose,
    availableDates,
    userTimeslotRequestsAllowed
  } = t0;
  let t1;
  if ($[0] !== availableDates || $[1] !== onClose || $[2] !== userTimeslotRequestsAllowed) {
    t1 = props => <><Modal.Header onClose={onClose}>Datum wählen</Modal.Header><AppDateModalContent {...props} availableDates={availableDates} userTimeslotRequestsAllowed={userTimeslotRequestsAllowed} /></>;
    $[0] = availableDates;
    $[1] = onClose;
    $[2] = userTimeslotRequestsAllowed;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isOpen || $[5] !== onClose || $[6] !== t1) {
    t2 = <Modal isOpen={isOpen} onClose={onClose}>{t1}</Modal>;
    $[4] = isOpen;
    $[5] = onClose;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}